import axios from 'axios';
import { baseURL } from '@/config/APIConfig';

const API = baseURL.local; // '/api/'

/**
 * Notification Service - Triggers notifications for application events
 */
const notificationService = {
    /**
     * Trigger a notification event
     * @param {string} eventType - Type of event (e.g., 'booking_created', 'document_uploaded')
     * @param {object} options - Additional options
     * @param {string} options.entityId - ID of the related entity (booking ID, container ID, etc.)
     * @param {string} options.entityName - Human readable name
     * @param {object} options.details - Additional details/metadata
     */
    async trigger(eventType, options = {}) {
        try {
            const response = await axios.post(`${API}notifications/trigger`, {
                event_type: eventType,
                entity_id: options.entityId || null,
                entity_name: options.entityName || null,
                details: options.details || null
            });
            return response.data;
        } catch (error) {
            console.error('Failed to trigger notification:', error);
            return null;
        }
    },

    // Convenience methods for common events
    
    // Document Events
    documentUploaded(documentName, documentId = null) {
        return this.trigger('document_uploaded', {
            entityId: documentId,
            entityName: documentName
        });
    },

    documentAICompleted(documentName, documentId = null) {
        return this.trigger('document_ai_completed', {
            entityId: documentId,
            entityName: documentName
        });
    },

    documentValidationWarning(documentName, issues = []) {
        return this.trigger('document_validation_warning', {
            entityName: documentName,
            details: { issues }
        });
    },

    documentReviewNeeded(documentName, reason = null) {
        return this.trigger('document_review_needed', {
            entityName: documentName,
            details: { reason }
        });
    },

    // Booking Events
    bookingCreated(bookingId, customerName = null) {
        return this.trigger('booking_created', {
            entityId: bookingId,
            entityName: customerName
        });
    },

    bookingStatusChanged(bookingId, newStatus) {
        return this.trigger('booking_status_changed', {
            entityId: bookingId,
            details: { status: newStatus }
        });
    },

    bookingConfirmed(bookingId) {
        return this.trigger('booking_confirmed', {
            entityId: bookingId
        });
    },

    // Container Events
    containerEvent(containerId, eventName) {
        return this.trigger('container_event', {
            entityId: containerId,
            details: { event: eventName }
        });
    },

    containerLocationUpdate(containerId, location) {
        return this.trigger('container_location', {
            entityId: containerId,
            details: { location }
        });
    },

    containerAlert(containerId, alertMessage) {
        return this.trigger('container_status_alert', {
            entityId: containerId,
            details: { alert: alertMessage }
        });
    },

    // Quote Events
    quoteRequestReceived(quoteId, customerName = null) {
        return this.trigger('quote_request_received', {
            entityId: quoteId,
            entityName: customerName
        });
    },

    vendorResponse(quoteId, vendorName = null) {
        return this.trigger('vendor_response', {
            entityId: quoteId,
            entityName: vendorName
        });
    },

    quoteExpiring(quoteId, expiryDate = null) {
        return this.trigger('quote_expiring', {
            entityId: quoteId,
            details: { expiry_date: expiryDate }
        });
    },

    // Email Events
    emailReceived(senderName = null, subject = null) {
        return this.trigger('email_received', {
            entityName: senderName,
            details: { subject }
        });
    },

    aiExtractionCompleted(emailId) {
        return this.trigger('ai_extraction_completed', {
            entityId: emailId
        });
    },

    // Status Audit Events
    statusAuditChanged(fileNumber, statusLabel, bookingId = null, checked = true) {
        return this.trigger('status_audit_changed', {
            entityId: fileNumber,
            entityName: statusLabel,
            details: { 
                booking_id: bookingId,
                status_label: statusLabel,
                checked: checked
            }
        });
    },

    auditActionRequired(fileNumber, actionDescription, bookingId = null) {
        return this.trigger('audit_action_required', {
            entityId: fileNumber,
            entityName: actionDescription,
            details: { booking_id: bookingId }
        });
    },

    auditCompleted(fileNumber, bookingId = null) {
        return this.trigger('audit_completed', {
            entityId: fileNumber,
            details: { booking_id: bookingId }
        });
    },

    // Bulk status audit notification for multiple changes
    statusAuditBulkUpdate(fileNumber, changedStatuses, bookingId = null) {
        // changedStatuses should be an array of { label, checked } objects
        const statusSummary = changedStatuses.map(s => `${s.label}: ${s.checked ? 'Completed' : 'Pending'}`).join(', ');
        return this.trigger('status_audit_changed', {
            entityId: fileNumber,
            entityName: `${changedStatuses.length} status(es) updated`,
            details: {
                booking_id: bookingId,
                changes: changedStatuses,
                summary: statusSummary
            }
        });
    }
};

export default notificationService;
